"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"
import { isUserAuthEnabledClient } from "@/src/utils/featureFlags"

export type User = {
  id: string
  name: string
  email: string
  image?: string
  hasPassword?: boolean
}

export type Session = {
  user: User
  expires: string
}

type AuthStatus = "authenticated" | "unauthenticated" | "loading" | "disabled"

type SessionContextValue = {
  data: Session | null
  status: AuthStatus
  update: (userid?: string) => Promise<void>
  signIn: () => void
  signOut: () => void
}

const AuthContext = createContext<SessionContextValue>({
  data: null,
  status: "unauthenticated",
  update: async () => {},
  signIn: () => {},
  signOut: () => {},
})

const STORAGE_KEY = "webapp_userid"

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [status, setStatus] = useState<AuthStatus>("loading")
  const router = useRouter()

  const checkAuthStatus = useCallback(async () => {
    if (!isUserAuthEnabledClient()) {
      setSession(null)
      setStatus("disabled")
      return
    }

    try {
      const storedUserId = localStorage.getItem(STORAGE_KEY)
      if (!storedUserId) {
        setSession(null)
        setStatus("unauthenticated")
        return
      }

      const response = await fetch("/api/user_authentication/profile", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userid: storedUserId }),
      })

      if (response.ok) {
        const userData = await response.json()
        setSession({
          user: userData.user,
          expires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        })
        setStatus("authenticated")
      } else {
        localStorage.removeItem(STORAGE_KEY)
        setSession(null)
        setStatus("unauthenticated")
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY)
      setSession(null)
      setStatus("unauthenticated")
    }
  }, [])

  useEffect(() => {
    checkAuthStatus()
  }, [checkAuthStatus])

  const update = useCallback(async (userid?: string) => {
    if (!isUserAuthEnabledClient()) return
    if (userid) {
      localStorage.setItem(STORAGE_KEY, userid)
    }
    await checkAuthStatus()
  }, [checkAuthStatus])

  const signIn = useCallback(() => {
    if (!isUserAuthEnabledClient()) return
    router.push("/login")
  }, [router])

  const signOut = useCallback(async () => {
    if (!isUserAuthEnabledClient()) return
    try {
      const storedUserId = localStorage.getItem(STORAGE_KEY)
      await fetch("/api/user_authentication/logout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userid: storedUserId }),
      })
    } catch {
      // Best effort
    }
    localStorage.removeItem(STORAGE_KEY)
    setSession(null)
    setStatus("unauthenticated")
    router.push("/")
  }, [router])

  return (
    <AuthContext.Provider value={{ data: session, status, update, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useSession() {
  return useContext(AuthContext)
}
